/**
 * UmsatzPanel — kontakt bazında ciro özeti.
 * Dönem filtresi (Monat/Quartal/Jahr/Gesamt/frei) + toplamlar + aylık grafik.
 * Admin ContactDetailSheet ve Agent LeadDetailSheet tarafından ortak kullanılır.
 */
import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Euro } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { getOrderTotal } from '@/lib/orderFields';

const PERIODS = [
  { value: 'month', label: 'Dieser Monat' },
  { value: 'quarter', label: 'Dieses Quartal' },
  { value: 'year', label: 'Dieses Jahr' },
  { value: 'all', label: 'Gesamt' },
  { value: 'custom', label: 'Zeitraum wählen…' },
];

function periodRange(period, customFrom, customTo) {
  const now = new Date();
  if (period === 'month') return [new Date(now.getFullYear(), now.getMonth(), 1), now];
  if (period === 'quarter') {
    const q = Math.floor(now.getMonth() / 3) * 3;
    return [new Date(now.getFullYear(), q, 1), now];
  }
  if (period === 'year') return [new Date(now.getFullYear(), 0, 1), now];
  if (period === 'custom') {
    return [customFrom ? new Date(customFrom) : null, customTo ? new Date(`${customTo}T23:59:59`) : null];
  }
  return [null, null];
}

const orderTotal = getOrderTotal;

export default function UmsatzPanel({ orders = [] }) {
  const [period, setPeriod] = useState('year');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Ciro yalnızca gerçekleşen siparişlerden (Bestellung) oluşur —
  // teklifler (Angebot) ve iptaller (Storniert) hariç.
  const validOrders = useMemo(
    () => orders.filter((o) => o.status !== 'Storniert' && o.order_type !== 'Angebot'),
    [orders]
  );

  const [from, to] = periodRange(period, customFrom, customTo);
  const periodOrders = useMemo(() => validOrders.filter((o) => {
    const d = new Date(o.created_date);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  }), [validOrders, from, to]);

  const totalRevenue = periodOrders.reduce((s, o) => s + orderTotal(o), 0);
  const lifetimeRevenue = validOrders.reduce((s, o) => s + orderTotal(o), 0);

  const chartData = useMemo(() => {
    const buckets = new Map();
    for (const o of periodOrders) {
      const d = new Date(o.created_date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      buckets.set(key, (buckets.get(key) || 0) + orderTotal(o));
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => ({
        monat: key.slice(5) + '/' + key.slice(2, 4),
        umsatz: Math.round(value * 100) / 100,
      }));
  }, [periodOrders]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {period === 'custom' && (
          <>
            <Input type="date" className="w-40" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            <span className="text-slate-400">–</span>
            <Input type="date" className="w-40" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-emerald-50 border-emerald-100">
          <CardContent className="p-4">
            <p className="text-[10px] font-bold text-emerald-600 uppercase flex items-center gap-1">
              <Euro className="w-3 h-3" /> Umsatz (Zeitraum)
            </p>
            <p className="text-2xl font-black text-emerald-900">
              € {totalRevenue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-emerald-700">{periodOrders.length} Bestellungen</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase">Gesamtumsatz</p>
            <p className="text-2xl font-black text-slate-900">
              € {lifetimeRevenue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-slate-500">{validOrders.length} Bestellungen gesamt</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <h4 className="text-xs font-bold text-slate-400 uppercase mb-3">Umsatzverlauf (monatlich)</h4>
          {chartData.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">Keine Umsätze im gewählten Zeitraum</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="monat" fontSize={11} />
                <YAxis fontSize={11} tickFormatter={(v) => `€${v}`} />
                <ChartTooltip formatter={(v) => [`€ ${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2 })}`, 'Umsatz']} />
                <Bar dataKey="umsatz" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
